import { useState, useEffect, useRef } from "react";
import { useLanguage } from "@/hooks/use-language";
import { SUPPORTED_LANGUAGES } from "@/contexts/language-context";
import { updatesTranslations } from "@/constants/updates-translations";

/* Page-scoped translation resolver. Falls back to English on miss. */
const useUpdT = () => {
  const { lang } = useLanguage();
  const dict = updatesTranslations[lang] || updatesTranslations.en;
  return (path) => {
    const parts = path.split(".");
    let cur = dict;
    for (const p of parts) cur = cur?.[p];
    if (cur === undefined) {
      let fb = updatesTranslations.en;
      for (const p of parts) fb = fb?.[p];
      return fb ?? path;
    }
    return cur;
  };
};

/* ────────────────────────────────────────────────────────────
   Page-scoped CSS injected on mount, removed on unmount
   ──────────────────────────────────────────────────────────── */
const UPDATES_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Press+Start+2P&family=Sora:wght@300;400;500;600;700;800&family=JetBrains+Mono:wght@400;500;700&display=swap');

.upd-root {
  --bg-0: #03050f;
  --bg-1: #060a1c;
  --bg-2: #0a1230;
  --bg-3: #101b46;
  --ink: #e6ecff;
  --ink-dim: #9aa9d6;
  --ink-mute: #5d6a96;
  --blue-1: #1f3bff;
  --blue-2: #3b6fff;
  --blue-3: #5fa8ff;
  --cyan-1: #7be7ff;
  --cyan-2: #aef3ff;
  --pink-1: #ff1f6d;
  --pink-2: #ff5aa0;
  --pink-3: #ff8fc1;
  --pink-glow: 0 0 18px rgba(255,31,109,.65), 0 0 48px rgba(255,31,109,.35);
  --blue-glow: 0 0 18px rgba(80,140,255,.55), 0 0 48px rgba(80,140,255,.25);
  --cyan-glow: 0 0 14px rgba(122,231,255,.55), 0 0 40px rgba(122,231,255,.25);
  --grid-rgb: 90, 130, 220;
  --font-disp: "Sora", system-ui, sans-serif;
  --font-mono: "JetBrains Mono", ui-monospace, monospace;
  --font-px: "Press Start 2P", monospace;

  font-family: var(--font-disp);
  font-weight: 400;
  letter-spacing: 0.005em;
  color: var(--ink);
  background: var(--bg-0);
  min-height: 100vh;
  overflow-x: hidden;
  position: relative;
}
.upd-root * { box-sizing: border-box; }

.upd-root::before {
  content: "";
  position: fixed; inset: 0; pointer-events: none; z-index: 0;
  background:
    radial-gradient(1200px 800px at 50% -10%, #122050 0%, transparent 60%),
    radial-gradient(900px 700px at 90% 20%, #1a0a2a 0%, transparent 55%),
    radial-gradient(1100px 900px at 10% 60%, #061029 0%, transparent 55%),
    linear-gradient(180deg, #03050f 0%, #050a1f 60%, #03050f 100%);
}
.upd-starfield {
  position: fixed; inset: 0; pointer-events: none; z-index: 0;
  background-image:
    radial-gradient(1px 1px at 20% 30%, rgba(255,255,255,.7), transparent 60%),
    radial-gradient(1px 1px at 70% 20%, rgba(255,255,255,.5), transparent 60%),
    radial-gradient(1px 1px at 40% 70%, rgba(255,255,255,.4), transparent 60%),
    radial-gradient(1px 1px at 85% 65%, rgba(255,255,255,.6), transparent 60%),
    radial-gradient(1px 1px at 15% 85%, rgba(255,255,255,.5), transparent 60%),
    radial-gradient(1.5px 1.5px at 60% 45%, rgba(255,255,255,.8), transparent 60%);
  background-size: 600px 600px, 700px 700px, 500px 500px, 800px 800px, 650px 650px, 900px 900px;
  opacity: .55;
}
.upd-grid {
  position: fixed; inset: 0; pointer-events: none; z-index: 0;
  background-image:
    linear-gradient(rgba(var(--grid-rgb), .045) 1px, transparent 1px),
    linear-gradient(90deg, rgba(var(--grid-rgb), .045) 1px, transparent 1px);
  background-size: 64px 64px, 64px 64px;
  background-position: -1px -1px;
  -webkit-mask-image: radial-gradient(1200px 900px at 50% 30%, #000 30%, transparent 80%);
          mask-image: radial-gradient(1200px 900px at 50% 30%, #000 30%, transparent 80%);
}

.upd-root .scene { position: relative; z-index: 1; }

/* ── FOG / MIST ── */
.upd-root .fog-layer {
  position: absolute; inset: 0;
  pointer-events: none; overflow: hidden;
  transition:
    opacity 1.8s cubic-bezier(.2,.7,.2,1),
    transform 2.4s cubic-bezier(.2,.7,.2,1),
    filter 1.6s cubic-bezier(.2,.7,.2,1);
  will-change: opacity, transform, filter;
  transform-origin: center;
}
.upd-root .fog-layer.dissipated {
  opacity: 0;
  transform: scale(1.35);
  filter: blur(10px);
}
.upd-root .fog-noise {
  position: absolute; inset: -50%;
  background-repeat: no-repeat;
  background-position: center;
  background-size: 100% 100%;
  mix-blend-mode: screen;
  will-change: transform;
  -webkit-mask-image: radial-gradient(ellipse 62% 62% at 50% 50%, #000 35%, transparent 100%);
          mask-image: radial-gradient(ellipse 62% 62% at 50% 50%, #000 35%, transparent 100%);
}
.upd-root .fog-noise.n1 {
  opacity: .65;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='1400' height='1400'><filter id='n' x='0' y='0' width='100%25' height='100%25'><feTurbulence type='fractalNoise' baseFrequency='0.005' numOctaves='4' seed='7' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 1.0 -0.35'/><feGaussianBlur stdDeviation='1.5'/></filter><rect width='1400' height='1400' filter='url(%23n)'/></svg>");
  animation: upd-fog-a 80s ease-in-out infinite alternate;
}
.upd-root .fog-noise.n2 {
  opacity: .50;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='1400' height='1400'><filter id='n' x='0' y='0' width='100%25' height='100%25'><feTurbulence type='fractalNoise' baseFrequency='0.0035' numOctaves='3' seed='13' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 0.9 -0.3'/><feGaussianBlur stdDeviation='2'/></filter><rect width='1400' height='1400' filter='url(%23n)'/></svg>");
  animation: upd-fog-b 110s ease-in-out infinite alternate;
}
.upd-root .fog-noise.n3 {
  opacity: .35;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='1400' height='1400'><filter id='n' x='0' y='0' width='100%25' height='100%25'><feTurbulence type='fractalNoise' baseFrequency='0.002' numOctaves='2' seed='3' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 0.75 -0.25'/><feGaussianBlur stdDeviation='3'/></filter><rect width='1400' height='1400' filter='url(%23n)'/></svg>");
  animation: upd-fog-c 150s ease-in-out infinite alternate;
  filter: blur(2px);
}
@keyframes upd-fog-a { 0%{transform:translate3d(0,0,0)} 100%{transform:translate3d(-9%,-5%,0)} }
@keyframes upd-fog-b { 0%{transform:translate3d(0,0,0)} 100%{transform:translate3d(8%,6%,0)} }
@keyframes upd-fog-c { 0%{transform:translate3d(0,0,0)} 100%{transform:translate3d(-5%,8%,0)} }

.upd-root .fog-tint {
  position: absolute; inset: -20%;
  pointer-events: none; mix-blend-mode: screen;
  -webkit-mask-image: radial-gradient(70% 75% at 50% 50%, #000 30%, transparent 90%);
          mask-image: radial-gradient(70% 75% at 50% 50%, #000 30%, transparent 90%);
  animation: upd-tint-drift 50s ease-in-out infinite alternate;
}
@keyframes upd-tint-drift {
  0% { transform: translate3d(0,0,0) scale(1); }
  100% { transform: translate3d(-2%,3%,0) scale(1.05); }
}
.upd-root .tint-cool .fog-tint {
  background:
    radial-gradient(60% 75% at 25% 30%, rgba(55,105,210,.55), transparent 70%),
    radial-gradient(70% 80% at 80% 70%, rgba(20,45,120,.6), transparent 75%),
    radial-gradient(40% 50% at 50% 50%, rgba(120,200,255,.22), transparent 80%);
}
.upd-root .tint-danger .fog-tint {
  background:
    radial-gradient(60% 75% at 25% 30%, rgba(80,30,120,.55), transparent 70%),
    radial-gradient(70% 80% at 80% 70%, rgba(255,35,115,.45), transparent 75%),
    radial-gradient(40% 50% at 50% 50%, rgba(255,150,200,.22), transparent 80%);
}
.upd-root .tint-mix .fog-tint {
  background:
    radial-gradient(60% 75% at 25% 30%, rgba(45,80,170,.55), transparent 70%),
    radial-gradient(70% 80% at 80% 70%, rgba(170,60,140,.4), transparent 75%),
    radial-gradient(40% 50% at 50% 50%, rgba(170,200,240,.25), transparent 80%);
}
.upd-root .dens-heavy .fog-noise.n1 { opacity: .8; }
.upd-root .dens-heavy .fog-noise.n2 { opacity: .6; }
.upd-root .dens-heavy .fog-noise.n3 { opacity: .45; }
.upd-root .dens-heavy .fog-tint { opacity: 1.15; }
.upd-root .dens-light .fog-noise.n1 { opacity: .38; }
.upd-root .dens-light .fog-noise.n2 { opacity: .28; }
.upd-root .dens-light .fog-noise.n3 { opacity: .2; }

/* ── HERO ── */
.upd-root .hero {
  position: relative;
  min-height: 100vh;
  width: 100%;
  overflow: hidden;
  display: grid;
  place-items: center;
  isolation: isolate;
}
.upd-root .hero-art {
  position: absolute; inset: 0;
  background: url("/updates/assets/phuong-hero.png") center 30% / cover no-repeat;
  filter: brightness(.55) saturate(120%) contrast(108%);
  transform: scale(1.05);
  transition: filter 1.8s ease, transform 2.4s ease;
}
.upd-root .hero.revealed .hero-art { filter: brightness(.85) saturate(125%) contrast(108%); transform: scale(1); }
.upd-root .hero-vignette {
  position: absolute; inset: 0;
  background:
    radial-gradient(800px 700px at 50% 70%, transparent 30%, rgba(3,5,15,.85) 75%, #03050f 100%),
    linear-gradient(180deg, rgba(3,5,15,.55) 0%, transparent 30%, transparent 60%, #03050f 100%);
  pointer-events: none;
}
.upd-root .hero-content {
  position: relative; z-index: 3;
  text-align: center;
  padding: 80px 24px;
  max-width: 920px;
  display: grid; gap: 36px;
  justify-items: center;
  transition: opacity 1.4s ease, transform 1.4s ease;
}
.upd-root .hero.revealed .hero-content { transform: translateY(-8px); }

.upd-root .hero-eyebrow {
  font-family: var(--font-mono);
  font-size: 12px; letter-spacing: .4em;
  color: var(--cyan-2);
  text-transform: uppercase;
  display: inline-flex; gap: 14px; align-items: center;
  opacity: 0;
  animation: upd-fadeIn 1.4s .2s forwards;
}
.upd-root .hero-eyebrow .dot {
  width: 8px; height: 8px; border-radius: 50%;
  background: var(--pink-1);
  box-shadow: var(--pink-glow);
  animation: upd-pulse 1.8s ease-in-out infinite;
}
@keyframes upd-pulse {
  0%,100% { opacity: 1; transform: scale(1); }
  50% { opacity: .55; transform: scale(.8); }
}
@keyframes upd-fadeIn { to { opacity: 1; } }

.upd-root .hero-title {
  font-family: var(--font-disp);
  font-weight: 800;
  font-size: clamp(48px, 8vw, 112px);
  line-height: .95;
  letter-spacing: -.02em;
  margin: 0;
  text-wrap: balance;
  background: linear-gradient(180deg, #ffffff 0%, #cfe0ff 45%, #6e8edd 100%);
  -webkit-background-clip: text; background-clip: text;
  -webkit-text-fill-color: transparent;
  text-shadow: 0 0 60px rgba(80,140,255,.18);
  opacity: 0;
  animation: upd-fadeIn 1.6s .4s forwards;
}
.upd-root .hero-title .accent {
  display: block;
  font-family: var(--font-px);
  font-size: clamp(14px, 1.6vw, 22px);
  letter-spacing: .12em;
  margin-top: 22px;
  background: linear-gradient(90deg, var(--cyan-1), var(--blue-3));
  -webkit-background-clip: text; background-clip: text;
  -webkit-text-fill-color: transparent;
  text-shadow: 0 0 14px rgba(122,231,255,.6);
}
.upd-root .hero-sub {
  font-size: 17px;
  color: var(--ink-dim);
  max-width: 560px;
  line-height: 1.55;
  margin: 0;
  opacity: 0;
  animation: upd-fadeIn 1.6s .6s forwards;
}

.upd-root .reveal-btn {
  --c1: #ff1f6d;
  --c2: #ff5aa0;
  position: relative;
  padding: 20px 44px 20px 56px;
  font-family: var(--font-disp);
  font-weight: 700;
  font-size: 15px;
  letter-spacing: .3em;
  text-transform: uppercase;
  color: #fff;
  background: linear-gradient(180deg, rgba(255,31,109,.18), rgba(255,31,109,.05));
  border: 1.5px solid var(--c1);
  border-radius: 999px;
  cursor: pointer;
  overflow: hidden;
  box-shadow: var(--pink-glow), inset 0 0 24px rgba(255,31,109,.18);
  transition: transform .25s ease, box-shadow .35s ease, padding .35s ease, background .35s ease;
  opacity: 0;
  animation: upd-fadeIn 1.6s .8s forwards, upd-btn-breath 3.2s ease-in-out 2s infinite;
}
@keyframes upd-btn-breath {
  0%,100% { box-shadow: var(--pink-glow), inset 0 0 24px rgba(255,31,109,.18); }
  50% { box-shadow: 0 0 28px rgba(255,31,109,.85), 0 0 72px rgba(255,31,109,.45), inset 0 0 36px rgba(255,31,109,.3); }
}
.upd-root .reveal-btn:hover { transform: translateY(-2px); padding-left: 64px; }
.upd-root .reveal-btn::before {
  content: "";
  position: absolute; left: 24px; top: 50%; transform: translateY(-50%);
  width: 14px; height: 14px; border-radius: 50%;
  background: #fff;
  box-shadow: 0 0 14px #fff, 0 0 28px var(--c2);
}
.upd-root .reveal-btn::after {
  content: "";
  position: absolute; inset: 0;
  background: linear-gradient(120deg, transparent 30%, rgba(255,255,255,.35) 50%, transparent 70%);
  transform: translateX(-120%);
  transition: transform .9s ease;
}
.upd-root .reveal-btn:hover::after { transform: translateX(120%); }

.upd-root .scroll-hint {
  position: absolute;
  left: 50%; bottom: 36px; transform: translateX(-50%);
  display: flex; flex-direction: column; align-items: center; gap: 8px;
  font-family: var(--font-mono); font-size: 11px; letter-spacing: .3em;
  color: var(--ink-dim); text-transform: uppercase;
  opacity: 0; transition: opacity 1.6s 1.2s ease;
  pointer-events: none;
  z-index: 4;
}
.upd-root .hero.revealed .scroll-hint { opacity: 1; }
.upd-root .scroll-hint .line {
  width: 1px; height: 48px;
  background: linear-gradient(180deg, transparent, var(--cyan-1), transparent);
  animation: upd-scrollLine 2.4s ease-in-out infinite;
}
@keyframes upd-scrollLine {
  0% { transform: scaleY(.2); transform-origin: top; }
  50% { transform: scaleY(1); transform-origin: top; }
  100% { transform: scaleY(.2); transform-origin: bottom; }
}

.upd-root .hero-fog {
  position: absolute; inset: -5%; z-index: 2;
  pointer-events: none;
  transition: opacity 2.2s ease, transform 2.6s cubic-bezier(.2,.7,.2,1);
}
.upd-root .hero.revealed .hero-fog { opacity: 0; transform: scale(1.4); }

.upd-root .hero-ring {
  position: absolute;
  width: 720px; height: 720px;
  left: 50%; top: 55%; transform: translate(-50%, -50%);
  border-radius: 50%;
  background:
    radial-gradient(circle, rgba(80,140,255,.22) 0%, rgba(80,140,255,0) 60%),
    conic-gradient(from 0deg, rgba(255,31,109,.0) 0deg, rgba(255,31,109,.35) 90deg, rgba(80,140,255,.0) 180deg, rgba(122,231,255,.35) 270deg, rgba(255,31,109,.0) 360deg);
  filter: blur(40px);
  opacity: 0;
  transition: opacity 2s 0.5s ease;
  z-index: 1;
}
.upd-root .hero.revealed .hero-ring { opacity: .55; animation: upd-spin 22s linear infinite 1s; }
@keyframes upd-spin { to { transform: translate(-50%, -50%) rotate(360deg); } }

/* ── SECTION HEADERS ── */
.upd-root .section {
  position: relative;
  padding: 140px 24px;
  max-width: 1200px;
  margin: 0 auto;
}
.upd-root .section-head {
  display: flex; align-items: baseline; gap: 18px; margin-bottom: 64px;
  font-family: var(--font-mono);
}
.upd-root .section-head .num {
  font-family: var(--font-px);
  font-size: 14px;
  color: var(--pink-1);
  text-shadow: var(--pink-glow);
  letter-spacing: .15em;
}
.upd-root .section-head .bar {
  flex: 1; height: 1px;
  background: linear-gradient(90deg, var(--pink-1) 0%, transparent 100%);
  opacity: .55;
}
.upd-root .section-head h2 {
  font-family: var(--font-disp);
  font-weight: 800;
  font-size: clamp(28px, 4vw, 48px);
  letter-spacing: -.01em;
  margin: 0;
  color: var(--ink);
}
.upd-root .section-head .tag {
  font-family: var(--font-mono);
  font-size: 11px;
  letter-spacing: .3em;
  color: var(--ink-mute);
  text-transform: uppercase;
}

/* ── STATUS CARDS ── */
.upd-root .status-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 22px;
}
@media (max-width: 880px) {
  .upd-root .status-grid { grid-template-columns: 1fr; }
}
.upd-root .stat-card {
  position: relative;
  padding: 32px 28px 30px;
  border-radius: 18px;
  background: linear-gradient(180deg, rgba(20,30,75,.5), rgba(10,18,48,.65));
  border: 1px solid rgba(95,168,255,.18);
  overflow: hidden;
  isolation: isolate;
  box-shadow:
    inset 0 1px 0 rgba(255,255,255,.05),
    0 30px 60px -40px rgba(0,0,0,.8);
  opacity: 0;
  transform: translateY(40px);
  transition: opacity .9s ease, transform .9s ease;
}
.upd-root .stat-card.in { opacity: 1; transform: translateY(0); }
.upd-root .stat-card::before {
  content: "";
  position: absolute; inset: 0; z-index: -1;
  background: radial-gradient(400px 240px at 20% 0%, rgba(80,140,255,.18), transparent 65%);
}
.upd-root .stat-card .corner {
  position: absolute; top: 14px; right: 14px;
  font-family: var(--font-mono); font-size: 10px; letter-spacing: .25em;
  color: var(--ink-mute); text-transform: uppercase;
}
.upd-root .stat-card .label {
  font-family: var(--font-mono);
  font-size: 11px; letter-spacing: .3em;
  text-transform: uppercase;
  color: var(--ink-dim);
  margin-bottom: 22px;
}
.upd-root .stat-card .value {
  font-family: var(--font-disp);
  font-weight: 800;
  font-size: 64px;
  line-height: 1;
  letter-spacing: -.02em;
  margin: 0 0 6px;
  display: flex; flex-wrap: wrap; align-items: baseline; gap: 6px 12px;
}
.upd-root .stat-card .value .unit {
  font-size: 13px; font-weight: 500;
  color: var(--ink-mute);
  letter-spacing: .2em; text-transform: uppercase;
  flex-basis: 100%;
  margin-top: 6px;
}
.upd-root .stat-card .value .unit.inline { flex-basis: auto; margin-top: 0; }
.upd-root .stat-card .meta {
  display: flex; align-items: center; gap: 10px;
  font-family: var(--font-mono); font-size: 12px; color: var(--ink-dim);
  margin-top: 18px;
}
.upd-root .stat-card .meta .pill {
  display: inline-flex; align-items: center; gap: 8px;
  padding: 4px 10px; border-radius: 999px;
  border: 1px solid currentColor;
  font-size: 11px; letter-spacing: .2em; text-transform: uppercase;
}
.upd-root .stat-card .meta .pill .pulse {
  width: 6px; height: 6px; border-radius: 50%;
  background: currentColor; box-shadow: 0 0 8px currentColor;
  animation: upd-pulse 1.6s ease-in-out infinite;
}
.upd-root .stat-card .sparkline {
  margin-top: 24px; display: block; width: 100%; height: 40px; opacity: .85;
}

.upd-root .stat-card.tone-cyan { border-color: rgba(122,231,255,.32); }
.upd-root .stat-card.tone-cyan .value { color: var(--cyan-2); text-shadow: var(--cyan-glow); }
.upd-root .stat-card.tone-cyan .meta { color: var(--cyan-1); }

.upd-root .stat-card.tone-blue { border-color: rgba(80,140,255,.32); }
.upd-root .stat-card.tone-blue .value { color: #cfe0ff; text-shadow: var(--blue-glow); }
.upd-root .stat-card.tone-blue .meta { color: var(--blue-3); }

.upd-root .stat-card.tone-danger {
  border-color: rgba(255,31,109,.5);
  box-shadow: var(--pink-glow), inset 0 0 40px rgba(255,31,109,.08), 0 30px 60px -40px rgba(0,0,0,.8);
  animation: upd-danger-breath 2.6s ease-in-out infinite;
}
.upd-root .stat-card.tone-danger::before {
  background: radial-gradient(500px 280px at 30% 0%, rgba(255,31,109,.25), transparent 65%);
}
.upd-root .stat-card.tone-danger .value { color: #ffd9e7; text-shadow: var(--pink-glow); }
.upd-root .stat-card.tone-danger .meta { color: var(--pink-2); }
@keyframes upd-danger-breath {
  0%,100% { box-shadow: 0 0 18px rgba(255,31,109,.45), 0 0 48px rgba(255,31,109,.18), inset 0 0 40px rgba(255,31,109,.08); }
  50% { box-shadow: 0 0 28px rgba(255,31,109,.85), 0 0 72px rgba(255,31,109,.4), inset 0 0 56px rgba(255,31,109,.18); }
}
.upd-root .stat-card.tone-danger .scanline {
  position: absolute; left: 0; right: 0; top: 0; height: 2px;
  background: linear-gradient(90deg, transparent, var(--pink-1), transparent);
  filter: blur(1px); opacity: .9;
  animation: upd-scan 3.2s linear infinite;
}
@keyframes upd-scan { 0% { transform: translateY(0); } 100% { transform: translateY(220px); } }

/* ── TIMELINE ── */
.upd-root .timeline {
  position: relative;
  padding: 80px 0 200px;
  margin: -40px auto 0;
  max-width: 760px;
}
.upd-root .timeline-rail {
  position: absolute;
  left: 50%; top: 0; bottom: 60px;
  width: 2px;
  transform: translateX(-50%);
  background: linear-gradient(180deg, transparent 0%, rgba(80,140,255,.6) 6%, rgba(80,140,255,.6) 68%, rgba(255,31,109,.7) 88%, transparent 100%);
  filter: drop-shadow(0 0 8px rgba(80,140,255,.7));
  z-index: 0;
}
.upd-root .timeline-rail::after {
  content: "";
  position: absolute;
  left: -3px; right: -3px;
  height: 80px;
  background: linear-gradient(180deg, transparent, rgba(122,231,255,.9), transparent);
  filter: blur(2px);
  animation: upd-rail-pulse 6s ease-in-out infinite;
  border-radius: 10px;
}
@keyframes upd-rail-pulse {
  0% { top: -10%; opacity: 0; }
  20% { opacity: .9; }
  80% { opacity: .9; }
  100% { top: 100%; opacity: 0; }
}

.upd-root .tl-node {
  position: relative;
  width: 100%;
  margin: 0 0 88px;
  padding: 56px 40px 34px;
  border-radius: 22px;
  background:
    linear-gradient(180deg, rgba(28,42,92,.85), rgba(10,18,46,.95)),
    #060a1c;
  border: 1px solid rgba(95,168,255,.22);
  box-shadow:
    0 40px 100px -50px rgba(0,0,0,.95),
    inset 0 1px 0 rgba(255,255,255,.05),
    inset 0 0 0 1px rgba(95,168,255,.04);
  overflow: hidden;
  isolation: isolate;
  z-index: 1;
}
@media (max-width: 760px) {
  .upd-root .tl-node { padding: 48px 22px 28px; margin-bottom: 64px; }
}
.upd-root .tl-dot {
  position: absolute;
  left: 50%; top: 0;
  transform: translate(-50%, -50%);
  width: 16px; height: 16px;
  border-radius: 50%;
  background: var(--cyan-1);
  box-shadow: 0 0 0 5px rgba(80,140,255,.18), 0 0 0 10px rgba(80,140,255,.06), var(--cyan-glow);
  z-index: 4;
}
.upd-root .tl-node.danger .tl-dot {
  background: var(--pink-1);
  box-shadow: 0 0 0 5px rgba(255,31,109,.18), 0 0 0 10px rgba(255,31,109,.06), var(--pink-glow);
}
.upd-root .tl-node.completed .tl-dot {
  background: var(--cyan-2);
  box-shadow: 0 0 0 5px rgba(122,231,255,.14), 0 0 0 10px rgba(122,231,255,.05), 0 0 10px rgba(122,231,255,.5);
}

.upd-root .tl-parting {
  position: absolute; inset: 0;
  pointer-events: none;
  overflow: hidden;
  border-radius: 22px;
  z-index: 3;
}

.upd-root .tl-when {
  font-family: var(--font-mono);
  font-size: 12px;
  letter-spacing: .25em;
  text-transform: uppercase;
  color: var(--cyan-1);
  margin-bottom: 6px;
  display: inline-flex; align-items: center; gap: 10px;
}
.upd-root .tl-when .badge {
  padding: 3px 8px;
  border: 1px solid rgba(122,231,255,.4);
  border-radius: 4px;
  font-size: 10px; letter-spacing: .25em;
  color: var(--cyan-2);
}
.upd-root .tl-node.danger .tl-when { color: var(--pink-2); }
.upd-root .tl-node.danger .tl-when .badge { border-color: rgba(255,31,109,.55); color: var(--pink-3); }

.upd-root .tl-phase {
  display: inline-flex; align-items: center; gap: 10px;
  margin: -4px 0 16px;
  padding: 6px 14px 6px 10px;
  border-radius: 999px;
  font-family: var(--font-mono);
  font-size: 11px;
  letter-spacing: .25em;
  text-transform: uppercase;
}
.upd-root .tl-phase .ic { display: grid; place-items: center; width: 16px; height: 16px; }
.upd-root .tl-phase.done {
  background: linear-gradient(180deg, rgba(122,231,255,.14), rgba(122,231,255,.04));
  border: 1px solid rgba(122,231,255,.4);
  color: var(--cyan-2);
  box-shadow: inset 0 0 12px rgba(122,231,255,.1);
}
.upd-root .tl-phase.done .ic { color: var(--cyan-1); filter: drop-shadow(0 0 6px rgba(122,231,255,.8)); }
.upd-root .tl-phase.live {
  background: linear-gradient(180deg, rgba(255,31,109,.18), rgba(255,31,109,.04));
  border: 1px solid rgba(255,31,109,.5);
  color: #ffd9e7;
  box-shadow: 0 0 18px rgba(255,31,109,.35), inset 0 0 16px rgba(255,31,109,.12);
  animation: upd-live-breath 2.2s ease-in-out infinite;
}
@keyframes upd-live-breath {
  0%,100% { box-shadow: 0 0 14px rgba(255,31,109,.3), inset 0 0 14px rgba(255,31,109,.1); }
  50% { box-shadow: 0 0 24px rgba(255,31,109,.7), inset 0 0 22px rgba(255,31,109,.2); }
}
.upd-root .tl-phase.live .ic-dot {
  width: 8px; height: 8px; border-radius: 50%;
  background: var(--pink-1);
  box-shadow: 0 0 8px var(--pink-1), 0 0 16px var(--pink-1);
  animation: upd-pulse 1.4s ease-in-out infinite;
}
.upd-root .tl-phase.live .ic-ring { position: relative; width: 14px; height: 14px; }
.upd-root .tl-phase.live .ic-ring::before,
.upd-root .tl-phase.live .ic-ring::after {
  content: "";
  position: absolute; inset: 0;
  border-radius: 50%;
  border: 1.5px solid var(--pink-1);
  animation: upd-ring-pulse 1.8s ease-out infinite;
}
.upd-root .tl-phase.live .ic-ring::after { animation-delay: .9s; }
@keyframes upd-ring-pulse {
  0% { transform: scale(.4); opacity: 1; }
  100% { transform: scale(1.6); opacity: 0; }
}
.upd-root .tl-phase.upcoming {
  background: linear-gradient(180deg, rgba(95,168,255,.14), rgba(95,168,255,.04));
  border: 1px solid rgba(95,168,255,.4);
  color: #cfe0ff;
}
.upd-root .tl-phase.upcoming .ic-arrow {
  color: var(--blue-3); filter: drop-shadow(0 0 6px rgba(95,168,255,.6));
}

.upd-root .tl-node.completed {
  background:
    linear-gradient(180deg, rgba(22,32,68,.85), rgba(8,14,38,.95)),
    #050818;
  border-color: rgba(95,168,255,.14);
  filter: saturate(.7);
  transition: filter .3s ease;
}
.upd-root .tl-node.completed:hover { filter: saturate(1); }
.upd-root .tl-node.completed .tl-title { color: var(--ink-dim); }
.upd-root .tl-node.completed .tl-items > li { color: var(--ink-dim); }
.upd-root .tl-node.completed .tl-items > li b,
.upd-root .tl-node.completed .tl-items > li strong { color: #9fc8e0; text-shadow: none; }

.upd-root .tl-title {
  font-family: var(--font-disp);
  font-weight: 800;
  font-size: clamp(22px, 2.4vw, 32px);
  letter-spacing: -.01em;
  margin: 0 0 24px;
  color: var(--ink);
  text-wrap: balance;
}
.upd-root .tl-items {
  display: grid; gap: 12px;
  margin: 0; padding: 0; list-style: none;
}
.upd-root .tl-items > li {
  display: grid;
  grid-template-columns: 28px 1fr;
  align-items: start;
  gap: 14px;
  padding: 14px 16px;
  border-radius: 12px;
  background: rgba(255,255,255,.025);
  border: 1px solid rgba(255,255,255,.04);
  font-size: 15px;
  line-height: 1.55;
  color: var(--ink);
}
.upd-root .tl-items > li .ic {
  width: 26px; height: 26px;
  display: grid; place-items: center;
  border-radius: 8px;
  background: linear-gradient(180deg, rgba(80,140,255,.18), rgba(80,140,255,.04));
  border: 1px solid rgba(80,140,255,.35);
  color: var(--cyan-2);
  font-family: var(--font-mono); font-size: 11px; font-weight: 700;
  flex-shrink: 0;
}
.upd-root .tl-items > li b,
.upd-root .tl-items > li strong {
  font-family: var(--font-disp);
  font-weight: 700;
  font-style: normal;
  color: var(--cyan-2);
  text-shadow: 0 0 12px rgba(122,231,255,.35);
}
.upd-root .tl-node.danger .tl-items > li b,
.upd-root .tl-node.danger .tl-items > li strong { color: var(--pink-3); text-shadow: 0 0 12px rgba(255,90,160,.35); }
.upd-root .tl-items > li.hot b,
.upd-root .tl-items > li.hot strong { color: var(--pink-3); text-shadow: 0 0 12px rgba(255,90,160,.4); }
.upd-root .tl-items > li.hot .ic {
  background: linear-gradient(180deg, rgba(255,31,109,.22), rgba(255,31,109,.04));
  border-color: rgba(255,31,109,.5);
  color: var(--pink-3);
}
.upd-root .tl-items > li.hot b { color: var(--pink-3); }

.upd-root .tl-chips {
  display: flex; flex-wrap: wrap; gap: 10px;
  margin: 18px 0 4px;
}
.upd-root .tl-chip {
  display: inline-flex; align-items: center; gap: 8px;
  padding: 6px 10px 6px 6px;
  border-radius: 999px;
  background: rgba(255,255,255,.04);
  border: 1px solid rgba(255,255,255,.08);
  font-family: var(--font-mono); font-size: 11px;
  color: var(--ink-dim);
  text-transform: uppercase; letter-spacing: .15em;
}
.upd-root .tl-chip img { height: 18px; width: auto; display: block; }

.upd-root .tl-node.shrouded {
  border-color: rgba(255,255,255,.06);
  background: linear-gradient(180deg, rgba(10,16,40,.55), rgba(6,10,28,.85));
  min-height: 320px;
}
.upd-root .tl-node.shrouded .shrouded-fog {
  position: absolute; inset: 0;
  pointer-events: none;
  overflow: hidden;
  border-radius: 22px;
  z-index: 3;
}
.upd-root .tl-node.shrouded .shrouded-fog::before {
  content: "";
  position: absolute; inset: 0;
  background: radial-gradient(420px 280px at 80% 25%, rgba(255,90,160,.22), transparent 60%);
  mix-blend-mode: screen;
  pointer-events: none;
  z-index: 1;
}
.upd-root .tl-node.shrouded .quote {
  position: relative; z-index: 4;
  font-family: var(--font-disp);
  font-style: italic;
  font-weight: 300;
  font-size: clamp(22px, 2.6vw, 32px);
  line-height: 1.35;
  letter-spacing: -.005em;
  color: rgba(230,236,255,.92);
  text-shadow: 0 2px 30px rgba(80,140,255,.3);
  text-wrap: balance;
  max-width: 720px;
}
.upd-root .tl-node.shrouded .quote::before {
  content: "“";
  font-size: 80px; line-height: 1;
  color: var(--pink-2); opacity: .55;
  margin-right: 4px; vertical-align: -.4em;
  text-shadow: var(--pink-glow);
}
.upd-root .tl-node.shrouded .sig {
  position: relative; z-index: 4;
  margin-top: 24px;
  font-family: var(--font-mono); font-size: 11px; letter-spacing: .3em;
  text-transform: uppercase;
  color: var(--ink-mute);
}

/* ── FOOTER ── */
.upd-root .upd-footer {
  position: relative;
  padding: 60px 24px 80px;
  text-align: center;
  font-family: var(--font-mono);
  font-size: 11px;
  letter-spacing: .3em;
  text-transform: uppercase;
  color: var(--ink-mute);
}
.upd-root .upd-footer .glyph {
  font-family: var(--font-px);
  font-size: 10px;
  color: var(--pink-2);
  text-shadow: var(--pink-glow);
  display: block;
  margin-bottom: 14px;
}

/* ── HUD chrome ── */
.upd-root .hud {
  position: fixed; top: 18px; left: 18px; right: 18px;
  display: flex; justify-content: space-between; align-items: center;
  font-family: var(--font-mono); font-size: 11px;
  color: var(--ink-mute); letter-spacing: .25em; text-transform: uppercase;
  pointer-events: none;
  z-index: 10;
  mix-blend-mode: difference;
}
.upd-root .hud .left, .upd-root .hud .right { display: inline-flex; gap: 14px; align-items: center; }
.upd-root .hud .blip {
  width: 6px; height: 6px; border-radius: 50%;
  background: var(--pink-1); box-shadow: var(--pink-glow);
  animation: upd-pulse 1.6s ease-in-out infinite;
}

.upd-root .progress {
  position: fixed; top: 0; left: 0; right: 0; height: 2px;
  background: rgba(255,255,255,.04);
  z-index: 11;
}
.upd-root .progress .bar {
  height: 100%;
  background: linear-gradient(90deg, var(--blue-3), var(--cyan-1) 60%, var(--pink-1));
  width: 0%;
  box-shadow: 0 0 12px rgba(122,231,255,.7);
  transition: width .15s linear;
}

/* ── Language switcher ── */
.upd-root .lang-switch {
  position: fixed;
  top: 14px;
  right: 18px;
  z-index: 20;
  display: inline-flex;
  gap: 6px;
  padding: 6px;
  border-radius: 999px;
  background: rgba(8, 14, 38, 0.7);
  border: 1px solid rgba(95, 168, 255, 0.2);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  pointer-events: auto;
}
.upd-root .lang-switch button {
  font-family: var(--font-mono);
  font-size: 11px;
  letter-spacing: .15em;
  text-transform: uppercase;
  padding: 5px 10px;
  border-radius: 999px;
  border: none;
  background: transparent;
  color: var(--ink-dim);
  cursor: pointer;
  transition: color .2s ease, background .2s ease;
}
.upd-root .lang-switch button:hover { color: var(--cyan-2); }
.upd-root .lang-switch button.active {
  background: linear-gradient(180deg, rgba(255,31,109,.25), rgba(255,31,109,.05));
  color: #fff;
  box-shadow: 0 0 12px rgba(255,31,109,.4), inset 0 0 8px rgba(255,31,109,.2);
}
@media (max-width: 540px) {
  .upd-root .lang-switch { top: 44px; right: 12px; padding: 4px; }
  .upd-root .lang-switch button { padding: 4px 7px; font-size: 10px; letter-spacing: .1em; }
}

/* ── MOBILE PERF OVERRIDES ──
   Chỉ tắt 3 thủ phạm thực sự (blur filter trong fog transition,
   fog overlay trong từng card, mix-blend-mode trên fixed HUD).
   Mọi trang trí khác (starfield, grid, hero ring, pulse animations,
   scanline, rail pulse) được giữ nguyên. */
@media (max-width: 880px) {
  /* [BẮT BUỘC] HUD mix-blend-mode: difference trên fixed element
     bắt browser recomposite cả viewport mỗi frame scroll → lag scroll. */
  .upd-root .hud { mix-blend-mode: normal; color: rgba(154,169,214,.7); }

  /* [BẮT BUỘC] Hero fog: bỏ filter:blur(10px) + scale transform khi
     dissipate (đây là thứ gây "giật cái vô luôn"). Vẫn fade mượt. */
  .upd-root .fog-layer {
    transition: opacity 1.4s cubic-bezier(.2,.7,.2,1);
    will-change: opacity;
  }
  .upd-root .fog-layer.dissipated {
    opacity: 0;
    transform: none;
    filter: none;
  }
  .upd-root .hero-fog {
    transition: opacity 1.8s ease;
  }
  .upd-root .hero.revealed .hero-fog {
    opacity: 0;
    transform: none;
  }

  /* [BẮT BUỘC] Fog overlay trong mỗi timeline card chính là nguyên nhân
     Safari OOM → crash khi scroll xuống card thứ 2. Bỏ hoàn toàn. */
  .upd-root .tl-parting { display: none; }
  .upd-root .tl-node.shrouded .shrouded-fog { display: none; }
  .upd-root .tl-node.shrouded {
    background: linear-gradient(180deg, rgba(20,26,55,.85), rgba(10,14,35,.95));
  }
}
`;

/* ────────────────────────────────────────────────────────────
   Components
   ──────────────────────────────────────────────────────────── */

function FogLayer({ dissipated = false, density = "normal", tint = "mix" }) {
  return (
    <div
      className={`fog-layer tint-${tint} dens-${density} ${dissipated ? "dissipated" : ""}`}
      aria-hidden="true"
    >
      <div className="fog-tint" />
      <div className="fog-noise n1" />
      <div className="fog-noise n2" />
      <div className="fog-noise n3" />
    </div>
  );
}

function HUD({ revealed }) {
  const t = useUpdT();
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  const pad = (n) => String(n).padStart(2, "0");
  const stamp = `${now.getUTCFullYear()}.${pad(now.getUTCMonth() + 1)}.${pad(now.getUTCDate())} · ${pad(now.getUTCHours())}:${pad(now.getUTCMinutes())}:${pad(now.getUTCSeconds())} UTC`;
  return (
    <div className="hud">
      <span className="left">
        <span className="blip" />
        <span>{t("hud.title")}</span>
      </span>
      <span className="right">
        <span>{stamp}</span>
        <span style={{ color: revealed ? "#7be7ff" : "#ff5aa0" }}>
          {revealed ? t("hud.fogLifted") : t("hud.fogDense")}
        </span>
      </span>
    </div>
  );
}

function ProgressBar() {
  const ref = useRef(null);
  useEffect(() => {
    const onScroll = () => {
      const h = document.documentElement.scrollHeight - window.innerHeight;
      const p = h > 0 ? (window.scrollY / h) * 100 : 0;
      if (ref.current) ref.current.style.width = `${p}%`;
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <div className="progress">
      <div ref={ref} className="bar" />
    </div>
  );
}

function LanguageSwitch() {
  const { lang, setLang } = useLanguage();
  return (
    <div className="lang-switch" role="group" aria-label="Language">
      {SUPPORTED_LANGUAGES.map((l) => (
        <button
          key={l.code}
          onClick={() => setLang(l.code)}
          className={lang === l.code ? "active" : ""}
          aria-pressed={lang === l.code}
        >
          {l.code.toUpperCase()}
        </button>
      ))}
    </div>
  );
}

function Hero({ onReveal }) {
  const t = useUpdT();
  const [revealed, setRevealed] = useState(false);

  const handleReveal = () => {
    if (revealed) return;
    setRevealed(true);
    onReveal && onReveal();
  };

  useEffect(() => {
    if (revealed) return;
    const onKey = (e) => {
      if (e.code === "Space" || e.code === "Enter") {
        e.preventDefault();
        handleReveal();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revealed]);

  return (
    <section className={`hero ${revealed ? "revealed" : ""}`}>
      <div className="hero-art" />
      <div className="hero-ring" />
      <div className="hero-vignette" />

      <div className="hero-content">
        <div className="hero-eyebrow">
          <span className="dot" />
          <span>{t("hero.eyebrow")}</span>
          <span className="dot" />
        </div>

        <h1 className="hero-title">
          {t("hero.title1")}
          <br />
          {t("hero.title2")}
          <span className="accent">{t("hero.accent")}</span>
        </h1>

        <p className="hero-sub">
          {t("hero.sub")}
          {!revealed && <> {t("hero.subExtra")}</>}
        </p>

        {!revealed && (
          <button className="reveal-btn" onClick={handleReveal}>
            {t("hero.revealBtn")}
          </button>
        )}

        {revealed && (
          <div
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 11,
              letterSpacing: ".3em",
              color: "var(--cyan-1)",
              textTransform: "uppercase",
              opacity: 0,
              animation: "upd-fadeIn 1.6s 1.4s forwards",
            }}
          >
            {t("hero.online")}
          </div>
        )}
      </div>

      <div className="hero-fog">
        <FogLayer dissipated={revealed} density="heavy" tint="mix" />
      </div>

      <div className="scroll-hint">
        <span>{t("hero.scrollHint")}</span>
        <span className="line" />
      </div>
    </section>
  );
}

function useInView(threshold = 0.2) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) setInView(true); }),
      { threshold }
    );
    io.observe(ref.current);
    return () => io.disconnect();
  }, [threshold]);
  return [ref, inView];
}

function Sparkline({ data, color, danger, gradId }) {
  const w = 240, h = 40;
  const max = Math.max(...data), min = Math.min(...data);
  const pts = data.map((v, i) => {
    const x = (i / (data.length - 1)) * w;
    const y = h - ((v - min) / (max - min || 1)) * (h - 6) - 3;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });
  const path = `M ${pts.join(" L ")}`;
  const [lastX, lastY] = pts[pts.length - 1].split(",").map(Number);
  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="sparkline">
      <defs>
        <linearGradient id={gradId} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity=".55" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={`${path} L ${w},${h} L 0,${h} Z`} fill={`url(#${gradId})`} />
      <path
        d={path}
        fill="none"
        stroke={color}
        strokeWidth="1.5"
        style={{ filter: `drop-shadow(0 0 6px ${color})`, ...(danger ? { strokeDasharray: "3 2" } : {}) }}
      />
      <circle cx={lastX} cy={lastY} r="2.5" fill={color} style={{ filter: `drop-shadow(0 0 6px ${color})` }} />
    </svg>
  );
}

function StatCard({ idx, corner, label, value, unit, unitInline, tone, pillLabel, meta, sparkData, sparkColor, danger, gradId }) {
  const [ref, inView] = useInView(0.25);
  return (
    <div
      ref={ref}
      className={`stat-card tone-${tone} ${inView ? "in" : ""}`}
      style={{ transitionDelay: `${idx * 0.12}s` }}
    >
      {danger && <div className="scanline" />}
      <div className="corner">{corner}</div>
      <div className="label">{label}</div>
      <div className="value">
        <span>{value}</span>
        {unit && <span className={`unit ${unitInline ? "inline" : ""}`}>{unit}</span>}
      </div>
      <div className="meta">
        <span className="pill"><span className="pulse" /> {pillLabel}</span>
        <span>· {meta}</span>
      </div>
      <Sparkline data={sparkData} color={sparkColor} danger={danger} gradId={gradId} />
    </div>
  );
}

function StatusSection() {
  const t = useUpdT();
  return (
    <section className="section">
      <div className="section-head">
        <span className="num">{t("status.number")}</span>
        <h2>{t("status.title")}</h2>
        <span className="bar" />
        <span className="tag">{t("status.tag")}</span>
      </div>

      <div className="status-grid">
        <StatCard
          idx={0}
          corner={t("status.dev.corner")}
          label={t("status.dev.label")}
          value={t("status.dev.value")}
          unit={t("status.dev.unit")}
          unitInline
          tone="cyan"
          pillLabel={t("status.dev.pill")}
          meta={t("status.dev.meta")}
          sparkData={[1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1]}
          sparkColor="#7be7ff"
          gradId="upd-spg-cyan"
        />
        <StatCard
          idx={1}
          corner={t("status.load.corner")}
          label={t("status.load.label")}
          value={t("status.load.value")}
          unit={t("status.load.unit")}
          tone="blue"
          pillLabel={t("status.load.pill")}
          meta={t("status.load.meta")}
          sparkData={[40, 42, 38, 45, 41, 39, 44, 40, 43, 38, 42, 41]}
          sparkColor="#5fa8ff"
          gradId="upd-spg-blue"
        />
        <StatCard
          idx={2}
          corner={t("status.econ.corner")}
          label={t("status.econ.label")}
          value={t("status.econ.value")}
          unit={t("status.econ.unit")}
          tone="danger"
          pillLabel={t("status.econ.pill")}
          meta={t("status.econ.meta")}
          sparkData={[88, 76, 72, 60, 54, 50, 42, 35, 30, 24, 18, 12]}
          sparkColor="#ff1f6d"
          danger
          gradId="upd-spg-pink"
        />
      </div>
    </section>
  );
}

function TimelineItem({ k, hot, html }) {
  return (
    <li className={hot ? "hot" : ""}>
      <span className="ic">{k}</span>
      <span dangerouslySetInnerHTML={{ __html: html }} />
    </li>
  );
}

function Chip({ src, label }) {
  return (
    <span className="tl-chip">
      <img src={src} alt="" />
      <span>{label}</span>
    </span>
  );
}

function TimelineNode({ when, badge, title, danger, completed, phase, items, chips, phaseLabel }) {
  const ref = useRef(null);
  const [unveil, setUnveil] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.intersectionRatio > 0.35) setUnveil(true); }),
      { threshold: [0, 0.15, 0.35, 0.55, 0.85] }
    );
    io.observe(ref.current);
    return () => io.disconnect();
  }, []);
  return (
    <div ref={ref} className={`tl-node ${danger ? "danger" : ""} ${completed ? "completed" : ""} ${unveil ? "unveil" : ""}`}>
      <span className="tl-dot" />
      <div className="tl-when">
        {badge && <span className="badge">{badge}</span>}
        <span>{when}</span>
      </div>
      {phase === "done" && (
        <div className="tl-phase done">
          <span className="ic">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M2 7.5 L5.5 11 L12 3.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
          <span>{phaseLabel}</span>
        </div>
      )}
      {phase === "live" && (
        <div className="tl-phase live">
          <span className="ic"><span className="ic-ring" /></span>
          <span className="ic-dot" />
          <span>{phaseLabel}</span>
        </div>
      )}
      {phase === "upcoming" && (
        <div className="tl-phase upcoming">
          <span className="ic">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" className="ic-arrow">
              <path d="M3 7 H11 M7.5 3.5 L11 7 L7.5 10.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
          <span>{phaseLabel}</span>
        </div>
      )}
      <h3 className="tl-title">{title}</h3>
      <ul className="tl-items">
        {items.map((it, i) => {
          const k = String(i + 1).padStart(2, "0");
          if (typeof it === "string") return <TimelineItem key={i} k={k} html={it} />;
          return <TimelineItem key={i} k={k} hot={it.hot} html={it.html} />;
        })}
      </ul>
      {chips && chips.length > 0 && (
        <div className="tl-chips">
          {chips.map((c, i) => <Chip key={i} src={c.src} label={c.label} />)}
        </div>
      )}
      <div className="tl-parting" aria-hidden="true">
        <FogLayer dissipated={unveil} density="normal" tint={danger ? "danger" : "cool"} />
      </div>
    </div>
  );
}

function ShroudedNode() {
  const t = useUpdT();
  return (
    <div className="tl-node shrouded">
      <span className="tl-dot" style={{ background: "#ff5aa0" }} />
      <div className="tl-when">
        <span className="badge" style={{ borderColor: "rgba(255,255,255,.25)", color: "#cfd6f0" }}>
          {t("timeline.shrouded.badge")}
        </span>
        <span style={{ color: "#cfd6f0" }}>{t("timeline.shrouded.when")}</span>
      </div>
      <p className="quote">{t("timeline.shrouded.quote")}</p>
      <div className="sig">{t("timeline.shrouded.sig")}</div>
      <div className="tl-parting shrouded-fog" aria-hidden="true">
        <FogLayer dissipated={false} density="heavy" tint="mix" />
      </div>
    </div>
  );
}

function TimelineSection() {
  const t = useUpdT();
  const { lang } = useLanguage();
  const dict = updatesTranslations[lang] || updatesTranslations.en;
  const tl = dict.timeline;
  return (
    <section className="section">
      <div className="section-head">
        <span className="num">{t("timeline.number")}</span>
        <h2>{t("timeline.title")}</h2>
        <span className="bar" />
        <span className="tag">{t("timeline.tag")}</span>
      </div>

      <div className="timeline">
        <div className="timeline-rail" />

        <TimelineNode
          when={tl.node1.when}
          badge={tl.node1.badge}
          phase="done"
          completed
          title={tl.node1.title}
          items={tl.node1.items}
          chips={tl.node1.chips}
          phaseLabel={tl.phase.done}
        />

        <TimelineNode
          when={tl.node2.when}
          badge={tl.node2.badge}
          phase="done"
          completed
          title={tl.node2.title}
          items={tl.node2.items}
          chips={tl.node2.chips}
          phaseLabel={tl.phase.done}
        />

        <TimelineNode
          when={tl.node3.when}
          badge={tl.node3.badge}
          phase="live"
          title={tl.node3.title}
          items={tl.node3.items}
          chips={tl.node3.chips}
          phaseLabel={tl.phase.live}
        />

        <ShroudedNode />
      </div>
    </section>
  );
}

/* ────────────────────────────────────────────────────────────
   Page
   ──────────────────────────────────────────────────────────── */
export default function UpdatesPage() {
  const t = useUpdT();
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    const style = document.createElement("style");
    style.id = "updates-css";
    style.textContent = UPDATES_CSS;
    document.head.appendChild(style);

    const prevBg = document.body.style.background;
    const prevOverflow = document.body.style.overflowX;
    document.body.style.background = "#03050f";
    document.body.style.overflowX = "hidden";

    const prevTitle = document.title;
    document.title = "PHUONG · Trạng Thái Thế Giới";

    return () => {
      document.getElementById("updates-css")?.remove();
      document.body.style.background = prevBg;
      document.body.style.overflowX = prevOverflow;
      document.title = prevTitle;
    };
  }, []);

  const onReveal = () => {
    setRevealed(true);
    setTimeout(() => {
      window.scrollTo({ top: window.innerHeight * 0.92, behavior: "smooth" });
    }, 1400);
  };

  return (
    <div className="upd-root">
      <div className="upd-starfield" />
      <div className="upd-grid" />
      <div className="scene">
        <ProgressBar />
        <HUD revealed={revealed} />
        <LanguageSwitch />

        <Hero onReveal={onReveal} />

        {revealed && (
          <>
            <StatusSection />
            <TimelineSection />
            <footer className="upd-footer">
              <span className="glyph">{t("footer.glyph")}</span>
              <span>{t("footer.sub")}</span>
            </footer>
          </>
        )}

        {!revealed && <div style={{ height: "20vh" }} />}
      </div>
    </div>
  );
}
